import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import {
  fetchSecuritySettings,
  saveErrorDismissed,
  saveSecuritySettings
} from '@/features/org/orgSlice'
import {
  selectSavingSecurity,
  selectSecurityCache,
  selectSecurityError,
  selectSecuritySettings,
  selectOrgSaveError
} from '@/features/org/orgSelectors'
import type { SecuritySettings } from '@/features/org/types'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'

const inputClass = 'rounded-md border border-input bg-card px-2 py-1.5 text-sm text-foreground'

/** The numeric policy fields with their (render-only) units. */
const NUMBER_FIELDS: readonly { key: keyof SecuritySettings; labelKey: string }[] = [
  { key: 'sessionTimeoutMinutes', labelKey: 'security.sessionTimeoutMinutes' },
  { key: 'passwordMinLength', labelKey: 'security.passwordMinLength' },
  { key: 'maxLoginAttempts', labelKey: 'security.maxLoginAttempts' },
  { key: 'lockoutDurationMinutes', labelKey: 'security.lockoutDurationMinutes' }
]

/**
 * The security policy form (PX-W6.2, AC-4). Numeric inputs render the SERVER's
 * value; the only local rule is "a positive integer" — the server owns the
 * ranges. The IP allowlist is a textarea, one address per line, split into the
 * string[] the wire carries.
 */
export function SecuritySettingsForm() {
  const { t } = useTranslation('orgSettings')
  const toMessage = useApiErrorMessage()
  const dispatch = useAppDispatch()

  const settings = useAppSelector(selectSecuritySettings)
  const cache = useAppSelector(selectSecurityCache)
  const error = useAppSelector(selectSecurityError)
  const saveError = useAppSelector(selectOrgSaveError)
  const saving = useAppSelector(selectSavingSecurity)

  // Derived-until-edited drafts — no effect-seeded state, so a late-arriving
  // load just appears in the inputs.
  const [edited, setEdited] = useState<SecuritySettings | null>(null)
  const [editedAllowlist, setEditedAllowlist] = useState<string | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const draft: SecuritySettings = edited ?? settings ?? {}
  const ipAllowlistText =
    editedAllowlist ?? (settings !== null ? (settings.ipAllowlist ?? []).join('\n') : '')

  useEffect(() => {
    void dispatch(fetchSecuritySettings())
  }, [dispatch])

  const dirty = useMemo(() => {
    if (settings === null || edited === null) return false
    const numbersChanged = NUMBER_FIELDS.some(
      ({ key }) => (edited[key] as number | undefined) !== (settings[key] as number | undefined)
    )
    const booleansChanged =
      edited.require2FA !== settings.require2FA ||
      edited.passwordRequireSpecialChar !== settings.passwordRequireSpecialChar
    const listChanged =
      ipAllowlistText !== (settings.ipAllowlist ?? []).join('\n')
    return numbersChanged || booleansChanged || listChanged
  }, [edited, settings, ipAllowlistText])

  const onNumberChanged = (key: keyof SecuritySettings, raw: string): void => {
    const parsed = Number.parseInt(raw, 10)
    setEdited((current) => ({
      ...draft,
      ...current,
      [key]: Number.isFinite(parsed) ? parsed : undefined
    }))
    dispatch(saveErrorDismissed())
  }

  const onToggleChanged = (key: 'require2FA' | 'passwordRequireSpecialChar', value: boolean): void => {
    setEdited((current) => ({ ...draft, ...current, [key]: value }))
    dispatch(saveErrorDismissed())
  }

  const onAllowlistChanged = (value: string): void => {
    setEditedAllowlist(value)
    dispatch(saveErrorDismissed())
  }

  const onSaveClicked = (): void => {
    for (const { key } of NUMBER_FIELDS) {
      const value = draft[key] as number | undefined
      if (value === undefined || !Number.isInteger(value) || value <= 0) {
        setLocalError(t(labelFor(key)))
        return
      }
    }
    setLocalError(null)
    void dispatch(
      saveSecuritySettings({
        ...draft,
        ipAllowlist: ipAllowlistText
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line !== '')
      })
    )
  }

  const saved = !dirty && !saving && saveError === null && settings !== null && localError === null

  return (
    <section
      aria-labelledby="security-settings-label"
      className="rounded-lg border border-border bg-card px-4 py-4"
    >
      <div id="security-settings-label" className="flex items-center gap-3">
        <SectionLabel>{t('tabs.security')}</SectionLabel>
        {dirty && <span className="text-xs text-muted-foreground">{t('dirtyHint')}</span>}
      </div>
      <div className="mt-3">
        {cache.loading ? (
          <LoadingPanel />
        ) : error !== null && error.status === 403 ? (
          <ForbiddenPanel requested="/{orgId}/settings/security" />
        ) : error !== null ? (
          <ErrorPanel error={error} onRetryClicked={() => void dispatch(fetchSecuritySettings())} />
        ) : (
          <>
            <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
              {NUMBER_FIELDS.map(({ key, labelKey }) => (
                <label key={key} className="flex flex-col gap-1 text-sm text-ink-soft">
                  {t(labelKey)}
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={(draft[key] as number | undefined) ?? ''}
                    onChange={(event) => onNumberChanged(key, event.target.value)}
                    className={cn(inputClass, 'w-full')}
                  />
                </label>
              ))}
            </div>

            <div className="mt-3 flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm text-ink-soft">
                <input
                  type="checkbox"
                  checked={draft.require2FA ?? false}
                  onChange={(event) => onToggleChanged('require2FA', event.target.checked)}
                />
                {t('security.require2FA')}
              </label>
              <label className="flex items-center gap-2 text-sm text-ink-soft">
                <input
                  type="checkbox"
                  checked={draft.passwordRequireSpecialChar ?? false}
                  onChange={(event) =>
                    onToggleChanged('passwordRequireSpecialChar', event.target.checked)
                  }
                />
                {t('security.passwordRequireSpecialChar')}
              </label>
            </div>

            <label className="mt-3 flex flex-col gap-1 text-sm text-ink-soft lg:w-2/3">
              {t('security.ipAllowlist')}
              <textarea
                rows={3}
                value={ipAllowlistText}
                onChange={(event) => onAllowlistChanged(event.target.value)}
                className={cn(inputClass, 'w-full font-mono text-xs')}
              />
            </label>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onSaveClicked}
                disabled={saving || !dirty}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {saving ? t('security.saving') : t('security.save')}
              </button>
              {saved && (
                <span role="status" className="text-sm text-done">
                  {t('security.saved')}
                </span>
              )}
              {localError !== null && (
                <p role="alert" className="text-sm text-blocked">
                  {localError}
                </p>
              )}
              {saveError !== null && (
                <p role="alert" className="text-sm text-blocked">
                  {t('saveFailed')} {toMessage(saveError)}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  )
}

/** The local-validation label for a numeric field key. */
function labelFor(key: keyof SecuritySettings): string {
  const match = NUMBER_FIELDS.find((field) => field.key === key)
  return match?.labelKey ?? 'generic'
}
